/* Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Commands } from 'bridge-commands/commands';
import { BridgeService } from 'crimson/lib/bridge/bridge.service';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import {
    ReadGenericConfigParameters,
    ReadReturn,
    WriteGenericConfigParameters,
    RemoveParameters,
    AddAppInfoToPersistenceParameters,
    AppInfo,
    GetAllAppInfoFromPersistenceReturn
} from './config-store.types';

@Injectable({
    providedIn: 'root'
})
export class ConfigStoreService {
    private logger: Logger;

    constructor(
        private bridgeService: BridgeService,
        private loggingService: LoggingService
    ) {
        this.logger = this.loggingService.getLogger('libraries/ConfigStoreService');
    }

    writeAppInfoToPersistence(data: AppInfo): Observable<boolean> {
        const params: AddAppInfoToPersistenceParameters = {
            appInfo: data
        };

        return this.bridgeService
            .executeCommand(Commands.Common.Loginid.AddAppInfoToPersistence, params, false, false)
            .pipe(
                tap(_ => this.logger.info('AppInfo Write Success')),
                catchError(error => {
                    this.logger.error(error);
                    return throwError(error);
                })
            );
    }

    readAppInfoFromPersistence(): Observable<AppInfo[]> {
        return this.bridgeService
            .executeCommand(Commands.Common.Loginid.GetAllAppInfoFromPersistence, {}, false, false)
            .pipe(
                map((response: GetAllAppInfoFromPersistenceReturn) => response?.AppsInfo),
                tap(_ => this.logger.info('AppInfo Read Success')),
                catchError(error => {
                    this.logger.error(error);
                    return throwError(error);
                })
            );
    }

    writeConfigFile(configName: string, data: Object): Observable<boolean> {
        this.logger.info(`Writing in config file for ${configName} ${JSON.stringify(data)}`);
        const path = Object.keys(data).map(key => {
            return { key, value: data[key] };
        });
        const params: WriteGenericConfigParameters = { configname: configName, path: path };
        return this.bridgeService.executeCommand(Commands.Common.Applicationcontrol.WriteGenericConfig, params).pipe(
            tap(_ => this.logger.info('Write Success')),
            catchError(error => {
                this.logger.error(error);
                return throwError(error);
            })
        );
    }

    readConfigFile(configName: string, paths: string[]): Observable<ReadReturn> {
        this.logger.info(`Reading config file for ${configName} ${JSON.stringify(paths)}`);
        const params: ReadGenericConfigParameters = {
            configname: configName,
            path: paths
        };
        return this.bridgeService.executeCommand(Commands.Common.Applicationcontrol.ReadGenericConfig, params).pipe(
            tap(_ => this.logger.info('Read Success')),
            catchError(error => {
                this.logger.error(error);
                return throwError(error);
            })
        );
    }

    removeFromConfig(configName: string, data: string[]): Observable<boolean> {
        this.logger.info(`Removing config ${JSON.stringify(data)} from file for ${configName}`);
        const params: RemoveParameters = {
            configname: configName,
            path: data
        };
        return this.bridgeService
            .executeCommand(Commands.Common.Applicationcontrol.RemoveFromGenericConfig, params)
            .pipe(
                tap(_ => this.logger.info('Config removed')),
                catchError(error => {
                    this.logger.error(error);
                    return throwError(error);
                })
            );
    }
}
